import { useEffect } from "react";

import Sentry from "@similarweb/sentry-logger";

import type { ISwSettings } from "app/@types/ISwSettings";
import { TEAMS } from "app/constants/teamName";

import { getCelloData } from "./getCelloData";

interface UseCelloInitializerProps {
    locale: string;
    swSettings: ISwSettings;
    isEnabled?: boolean;
}

declare global {
    interface Window {
        cello: {
            cmd: Array<(cello: any) => void>;
        };
    }
}

const MAX_RETRIES = 30;
const RETRY_INTERVAL = 500;

export const useCelloInitializer = ({ locale, swSettings, isEnabled = false }: UseCelloInitializerProps): void => {
    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let retries = 0;

        const initializeCello = async () => {
            try {
                const celloData = await getCelloData();

                if (!celloData) {
                    Sentry.warning("CelloIntegration: No Cello data available", { tags: { team: TEAMS.HAWKS } });
                    return;
                }

                const { user } = swSettings;
                const userName = user.username || "";
                const firstName = user.firstname || "";
                const lastName = user.lastname || "";
                const { ProductId, Token } = celloData;
                const userLanguage = new Intl.Locale(locale).language;

                window.cello = window.cello || { cmd: [] };
                window.cello.cmd.push(async function (cello) {
                    try {
                        await cello.boot({
                            productId: ProductId,
                            token: Token,
                            language: userLanguage,
                            productUserDetails: {
                                firstName: firstName,
                                lastName: lastName,
                                fullName: `${firstName} ${lastName}`.trim(),
                                email: userName,
                            },
                        });
                    } catch (error) {
                        Sentry.errorTracker({
                            message: "CelloIntegration: Failed to boot Cello",
                            e: error,
                            level: "error",
                            params: {
                                tags: { team: TEAMS.HAWKS },
                                extra: {
                                    productId: ProductId,
                                    userId: user.id || "",
                                    userEmail: userName,
                                },
                            },
                        });
                    }
                });
            } catch (error) {
                Sentry.errorTracker({
                    message: "CelloIntegration: Failed to initialize",
                    e: error,
                    level: "error",
                    params: {
                        tags: { team: TEAMS.HAWKS },
                    },
                });
            }
        };

        const checkCelloScript = () => {
            if (window.cello) {
                initializeCello();
                return;
            }

            if (retries < MAX_RETRIES) {
                retries++;
                timeoutId = setTimeout(checkCelloScript, RETRY_INTERVAL);
            } else {
                const scriptElement = document.querySelector('script[src*="cello.so"]');
                const isScriptBlocked = !scriptElement;

                if (isScriptBlocked) {
                    console.debug("CelloIntegration: Script blocked (ad blocker or CSP)");
                } else {
                    Sentry.warning(
                        `CelloIntegration: Script not loaded after ${MAX_RETRIES} attempts (${RETRY_INTERVAL}ms interval)`,
                        {
                            tags: { team: TEAMS.HAWKS },
                            extra: {
                                retriesAttempted: MAX_RETRIES,
                                totalWaitTime: `${(MAX_RETRIES * RETRY_INTERVAL) / 1000}s`,
                            },
                        },
                    );
                }
            }
        };

        checkCelloScript();

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [locale, isEnabled, swSettings]);
};
